import { useEffect } from 'react'
import { useForm } from '@inertiajs/react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'

interface Workflow {
  id: number
  name: string
  description: string | null
}

interface WorkflowModalProps {
  accountId: number
  workflow?: Workflow | null
  onClose: () => void
}

export default function WorkflowModal({
  accountId,
  workflow,
  onClose,
}: WorkflowModalProps) {
  const isEditing = !!workflow

  const { data, setData, post, patch, processing, errors } = useForm({
    name: workflow?.name || '',
    description: workflow?.description || '',
  })

  useEffect(() => {
    if (workflow) {
      setData('name', workflow.name)
      setData('description', workflow.description || '')
    }
  }, [workflow, setData])

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()

    if (isEditing) {
      patch(`/accounts/${accountId}/workflows/${workflow.id}`, {
        preserveScroll: true,
        onSuccess: () => {
          onClose()
        },
      })
    } else {
      post(`/accounts/${accountId}/workflows`, {
        preserveScroll: true,
        onSuccess: () => {
          onClose()
        },
      })
    }
  }

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[600px]">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>
              {isEditing ? 'Edit Workflow' : 'Create New Workflow'}
            </DialogTitle>
            <DialogDescription className="text-gray-500 dark:text-gray-500">
              {isEditing
                ? 'Update your workflow settings below.'
                : 'Create a new workflow to automate email processing.'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6 py-4">
            {/* Name */}
            <div className="space-y-2">
              <Label htmlFor="name">
                Name <span className="text-red-500">*</span>
              </Label>
              <Input
                id="name"
                value={data.name}
                onChange={(e) => setData('name', e.target.value)}
                placeholder="e.g., Newsletter Auto-Archive"
                className={errors.name ? 'border-red-500' : ''}
              />
              {errors.name && (
                <p className="text-sm text-red-500">{errors.name}</p>
              )}
            </div>

            {/* Description */}
            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={data.description}
                onChange={(e) => setData('description', e.target.value)}
                placeholder="Describe what this workflow does..."
                rows={3}
              />
            </div>
          </div>

          <DialogFooter className="mt-6">
            <Button type="button" variant="ghost" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={processing}>
              {processing
                ? isEditing
                  ? 'Updating...'
                  : 'Creating...'
                : isEditing
                  ? 'Update Workflow'
                  : 'Create Workflow'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
